import { useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Pagination from "../../components/gigs/Pagination";
import Button from "../../components/ui/Button/Button";
import InfoTooltip from "../../components/ui/InfoTooltip/InfoTooltip";
import { accountApi } from "../../lib/account";
import type {
  UserNotificationListResponseDto,
  UserNotificationResponseDto,
  UserNotificationSummaryResponseDto,
  UserNotificationViewDto,
} from "../../types/types";
import { apiErrorToMessage } from "../../utils/ApiUtils";
import { formatDateTime } from "../../utils/DateUtils";
import { resolveNotificationPath } from "../../utils/NotificationUtils";

const NOTIFICATIONS_PAGE_SIZE = 10;
type NotificationView = UserNotificationViewDto;

const parseNotificationView = (value: string | null): NotificationView =>
  value === "recent-updates" ? "recent-updates" : "needs-action";

const pageParamNameByView: Record<NotificationView, string> = {
  "needs-action": "needsActionPage",
  "recent-updates": "recentUpdatesPage",
};

const parsePageNumber = (value: string | null): number => {
  const parsed = Number.parseInt(value ?? "", 10);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
};

const NotificationRow = ({
  notification,
  pendingReadId,
  pendingArchiveId,
  onMarkRead,
  onArchive,
  onOpen,
}: {
  notification: UserNotificationResponseDto;
  pendingReadId: string | null;
  pendingArchiveId: string | null;
  onMarkRead: (notificationId: string) => void;
  onArchive: (notificationId: string) => void;
  onOpen: (notification: UserNotificationResponseDto) => void;
}) => {
  const isUnread = notification.readAt === null;
  const canArchive = !isUnread && !notification.needsAction;
  const isReadPending = pendingReadId === notification.id;
  const isArchivePending = pendingArchiveId === notification.id;
  const isPending = isReadPending || isArchivePending;

  return (
    <article className={`grid gap-4 border-b border-myteengig-border px-5 py-5 last:border-b-0 md:grid-cols-[minmax(0,1fr)_auto] md:items-center ${isUnread ? "bg-white" : "bg-myteengig-background/40"}`}>
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <h2 className={`text-base ${isUnread ? "font-semibold text-myteengig-text-primary" : "font-medium text-myteengig-text-primary"}`}>
            {notification.title}
          </h2>
          <span
            className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide ${
              notification.needsAction
                ? "bg-red-50 text-red-700"
                : isUnread
                  ? "bg-myteengig-primary/10 text-myteengig-primary"
                  : "bg-myteengig-background text-myteengig-text-muted"
            }`}
          >
            {notification.needsAction ? "Needs Action" : isUnread ? "New Update" : "Seen"}
          </span>
        </div>
        <p className="mt-1 text-sm text-myteengig-text-muted">{notification.body}</p>
        <p className="mt-2 text-xs font-medium uppercase tracking-wide text-myteengig-text-muted">
          {formatDateTime(notification.createdAt)}
        </p>
      </div>
      <div className="grid w-full gap-2 md:flex md:w-auto md:justify-end">
        {isUnread ? (
          <Button
            variant="tertiary"
            size="sm"
            disabled={isPending}
            onClick={() => onMarkRead(notification.id)}
            className="w-full border border-myteengig-primary/15 bg-myteengig-primary/5 whitespace-normal text-center leading-tight hover:bg-myteengig-primary/10"
          >
            {isReadPending ? "Saving..." : "Mark Read"}
          </Button>
        ) : null}
        {canArchive ? (
          <Button
            variant="tertiary"
            size="sm"
            disabled={isPending}
            onClick={() => onArchive(notification.id)}
            className="w-full whitespace-normal text-center leading-tight"
          >
            {isArchivePending ? "Saving..." : "Archive"}
          </Button>
        ) : null}
        <Button
          variant={notification.needsAction ? "primary" : "outline"}
          size="sm"
          disabled={isPending}
          onClick={() => onOpen(notification)}
          className="w-full whitespace-normal text-center leading-tight"
        >
          {notification.ctaLabel}
        </Button>
      </div>
    </article>
  );
};

const NotificationsPage = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeView = parseNotificationView(searchParams.get("tab"));
  const needsActionPage = parsePageNumber(searchParams.get(pageParamNameByView["needs-action"]));
  const recentUpdatesPage = parsePageNumber(searchParams.get(pageParamNameByView["recent-updates"]));
  const activePage = activeView === "needs-action" ? needsActionPage : recentUpdatesPage;
  const currentNotificationsQueryKey = ["notifications", activeView, activePage, NOTIFICATIONS_PAGE_SIZE] as const;
  const notificationsSummaryQuery = useQuery({
    queryKey: ["notifications-summary"],
    queryFn: () => accountApi.getNotificationSummary(),
    staleTime: 15_000,
  });
  const notificationsQuery = useQuery({
    queryKey: currentNotificationsQueryKey,
    queryFn: () => accountApi.listNotifications({
      view: activeView,
      page: activePage,
      size: NOTIFICATIONS_PAGE_SIZE,
    }),
    staleTime: 15_000,
    placeholderData: (previousData) => previousData,
  });
  const notificationSummaryErrorMessage = notificationsSummaryQuery.error
    ? "Notification counts are temporarily unavailable."
    : null;

  const invalidateNotificationQueries = () => {
    queryClient.invalidateQueries({ queryKey: ["notifications"] });
    queryClient.invalidateQueries({ queryKey: ["notifications-summary"] });
  };

  const setNotificationSummary = (
    updater: (current: UserNotificationSummaryResponseDto | undefined) => UserNotificationSummaryResponseDto | undefined,
  ) => {
    queryClient.setQueryData<UserNotificationSummaryResponseDto | undefined>(["notifications-summary"], updater);
  };

  const setNotificationListData = (
    updater: (current: UserNotificationListResponseDto | undefined) => UserNotificationListResponseDto | undefined,
  ) => {
    queryClient.setQueryData<UserNotificationListResponseDto | undefined>(currentNotificationsQueryKey, updater);
  };

  const updateSearchState = (updater: (nextParams: URLSearchParams) => void) => {
    const nextParams = new URLSearchParams(searchParams);
    updater(nextParams);

    if (parseNotificationView(nextParams.get("tab")) === "needs-action") {
      nextParams.delete("tab");
    }
    (["needs-action", "recent-updates"] as const).forEach((view) => {
      const paramName = pageParamNameByView[view];
      if (parsePageNumber(nextParams.get(paramName)) === 0) {
        nextParams.delete(paramName);
      }
    });

    setSearchParams(nextParams);
  };

  const setPageForView = (view: NotificationView, nextPage: number) => {
    updateSearchState((nextParams) => {
      nextParams.set("tab", view);
      nextParams.set(pageParamNameByView[view], String(Math.max(0, nextPage)));
    });
  };

  const setActiveNotificationView = (view: NotificationView) => {
    updateSearchState((nextParams) => {
      nextParams.set("tab", view);
    });
  };

  const adjustSummaryUnreadCounts = (
    current: UserNotificationSummaryResponseDto,
    view: NotificationView,
    unreadDelta: number,
  ): UserNotificationSummaryResponseDto => ({
    ...current,
    unreadCount: Math.max(0, current.unreadCount - unreadDelta),
    unreadNeedsActionCount: view === "needs-action"
      ? Math.max(0, current.unreadNeedsActionCount - unreadDelta)
      : current.unreadNeedsActionCount,
    unreadRecentUpdatesCount: view === "recent-updates"
      ? Math.max(0, current.unreadRecentUpdatesCount - unreadDelta)
      : current.unreadRecentUpdatesCount,
  });

  const markReadMutation = useMutation({
    mutationFn: (notificationId: string) => accountApi.markNotificationRead(notificationId),
    onMutate: async (notificationId: string) => {
      await queryClient.cancelQueries({ queryKey: ["notifications"] });
      await queryClient.cancelQueries({ queryKey: ["notifications-summary"] });

      const previousNotifications = queryClient.getQueryData<UserNotificationListResponseDto>(currentNotificationsQueryKey);
      const previousSummary = queryClient.getQueryData<UserNotificationSummaryResponseDto>(["notifications-summary"]);
      const now = new Date().toISOString();
      const unreadWasPresent = previousNotifications?.notifications.some(
        (notification) => notification.id === notificationId && notification.readAt === null,
      ) ?? false;

      setNotificationListData((current) =>
        current
          ? {
              ...current,
              notifications: current.notifications.map((notification) =>
                notification.id === notificationId && notification.readAt === null
                  ? { ...notification, readAt: now }
                  : notification,
              ),
            }
          : current,
      );
      if (unreadWasPresent) {
        setNotificationSummary((current) =>
          current
            ? adjustSummaryUnreadCounts(current, activeView, 1)
            : current,
        );
      }

      return { previousNotifications, previousSummary };
    },
    onError: (_error, _notificationId, context) => {
      if (context?.previousNotifications) {
        queryClient.setQueryData(currentNotificationsQueryKey, context.previousNotifications);
      }
      if (context?.previousSummary) {
        queryClient.setQueryData(["notifications-summary"], context.previousSummary);
      }
    },
    onSuccess: invalidateNotificationQueries,
  });

  const archiveMutation = useMutation({
    mutationFn: (notificationId: string) => accountApi.archiveNotification(notificationId),
    onMutate: async (notificationId: string) => {
      await queryClient.cancelQueries({ queryKey: ["notifications"] });
      await queryClient.cancelQueries({ queryKey: ["notifications-summary"] });

      const previousNotifications = queryClient.getQueryData<UserNotificationListResponseDto>(currentNotificationsQueryKey);
      const previousSummary = queryClient.getQueryData<UserNotificationSummaryResponseDto>(["notifications-summary"]);
      const archivedNotification = previousNotifications?.notifications.find((notification) => notification.id === notificationId) ?? null;
      setNotificationListData((current) =>
        current
          ? {
              ...current,
              notifications: current.notifications.filter((notification) => notification.id !== notificationId),
              page: {
                ...current.page,
                numberOfElements: Math.max(0, current.notifications.length - 1),
              },
            }
          : current,
      );
      if (archivedNotification) {
        setNotificationSummary((current) =>
          current
            ? {
                ...adjustSummaryUnreadCounts(current, "recent-updates", archivedNotification.readAt === null ? 1 : 0),
                recentUpdatesCount: Math.max(0, current.recentUpdatesCount - 1),
              }
            : current,
        );
      }

      return { previousNotifications, previousSummary };
    },
    onError: (_error, _notificationId, context) => {
      if (context?.previousNotifications) {
        queryClient.setQueryData(currentNotificationsQueryKey, context.previousNotifications);
      }
      if (context?.previousSummary) {
        queryClient.setQueryData(["notifications-summary"], context.previousSummary);
      }
    },
    onSuccess: invalidateNotificationQueries,
  });

  const markSectionReadMutation = useMutation({
    mutationFn: (view: NotificationView) => accountApi.markAllNotificationsRead(view),
    onMutate: async (view: NotificationView) => {
      await queryClient.cancelQueries({ queryKey: ["notifications"] });
      await queryClient.cancelQueries({ queryKey: ["notifications-summary"] });

      const previousNotifications = queryClient.getQueryData<UserNotificationListResponseDto>(currentNotificationsQueryKey);
      const previousSummary = queryClient.getQueryData<UserNotificationSummaryResponseDto>(["notifications-summary"]);
      const now = new Date().toISOString();
      const unreadCountDelta = previousSummary
        ? (view === "needs-action" ? previousSummary.unreadNeedsActionCount : previousSummary.unreadRecentUpdatesCount)
        : 0;

      setNotificationListData((current) =>
        current
          ? {
              ...current,
              notifications: current.notifications.map((notification) =>
                notification.readAt === null ? { ...notification, readAt: now } : notification,
              ),
            }
          : current,
      );
      if (unreadCountDelta > 0) {
        setNotificationSummary((current) =>
          current
            ? {
                ...adjustSummaryUnreadCounts(current, view, unreadCountDelta),
                unreadNeedsActionCount: view === "needs-action" ? 0 : current.unreadNeedsActionCount,
                unreadRecentUpdatesCount: view === "recent-updates" ? 0 : current.unreadRecentUpdatesCount,
              }
            : current,
        );
      }

      return { previousNotifications, previousSummary };
    },
    onError: (_error, _variables, context) => {
      if (context?.previousNotifications) {
        queryClient.setQueryData(currentNotificationsQueryKey, context.previousNotifications);
      }
      if (context?.previousSummary) {
        queryClient.setQueryData(["notifications-summary"], context.previousSummary);
      }
    },
    onSuccess: invalidateNotificationQueries,
  });

  const archiveAllRecentUpdatesMutation = useMutation({
    mutationFn: () => accountApi.archiveAllRecentUpdates(),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: ["notifications"] });
      await queryClient.cancelQueries({ queryKey: ["notifications-summary"] });

      const previousNotifications = queryClient.getQueryData<UserNotificationListResponseDto>(currentNotificationsQueryKey);
      const previousSummary = queryClient.getQueryData<UserNotificationSummaryResponseDto>(["notifications-summary"]);
      const unreadRecentUpdatesCount = previousSummary?.unreadRecentUpdatesCount ?? 0;

      setNotificationListData((current) =>
        current
          ? {
              ...current,
              notifications: [],
              page: {
                ...current.page,
                numberOfElements: 0,
                hasNext: false,
                last: true,
              },
            }
          : current,
      );
      setNotificationSummary((current) =>
        current
          ? {
              ...adjustSummaryUnreadCounts(current, "recent-updates", unreadRecentUpdatesCount),
              recentUpdatesCount: 0,
              unreadRecentUpdatesCount: 0,
            }
          : current,
      );

      return { previousNotifications, previousSummary };
    },
    onError: (_error, _variables, context) => {
      if (context?.previousNotifications) {
        queryClient.setQueryData(currentNotificationsQueryKey, context.previousNotifications);
      }
      if (context?.previousSummary) {
        queryClient.setQueryData(["notifications-summary"], context.previousSummary);
      }
    },
    onSuccess: invalidateNotificationQueries,
  });

  const notifications = notificationsQuery.data?.notifications ?? [];
  const pageInfo = notificationsQuery.data?.page ?? {
    number: activePage,
    size: NOTIFICATIONS_PAGE_SIZE,
    numberOfElements: notifications.length,
    hasNext: false,
    hasPrevious: activePage > 0,
    first: activePage === 0,
    last: true,
    sortBy: [],
  };
  const needsActionCount = notificationsSummaryQuery.data?.needsActionCount
    ?? (activeView === "needs-action" ? notifications.length : 0);
  const recentUpdatesCount = notificationsSummaryQuery.data?.recentUpdatesCount
    ?? (activeView === "recent-updates" ? notifications.length : 0);
  const unreadNeedsActionCount = notificationsSummaryQuery.data?.unreadNeedsActionCount
    ?? (activeView === "needs-action" ? notifications.filter((notification) => notification.readAt === null).length : 0);
  const unreadRecentUpdatesCount = notificationsSummaryQuery.data?.unreadRecentUpdatesCount
    ?? (activeView === "recent-updates" ? notifications.filter((notification) => notification.readAt === null).length : 0);
  const totalNotifications = needsActionCount + recentUpdatesCount;
  const activeNotificationCount = activeView === "needs-action" ? needsActionCount : recentUpdatesCount;
  const shouldShowCaughtUpState = !notificationsSummaryQuery.isLoading && !notificationsSummaryQuery.error && totalNotifications === 0;
  const shouldShowNotificationWorkspace = notificationsSummaryQuery.error || totalNotifications > 0;
  const pendingReadNotificationId = markReadMutation.isPending && typeof markReadMutation.variables === "string"
    ? markReadMutation.variables
    : null;
  const pendingArchiveNotificationId = archiveMutation.isPending && typeof archiveMutation.variables === "string"
    ? archiveMutation.variables
    : null;

  useEffect(() => {
    if (!notificationsQuery.isFetching && activePage > 0 && activeNotificationCount > 0 && notifications.length === 0) {
      setPageForView(activeView, activePage - 1);
    }
  }, [activeNotificationCount, activePage, activeView, notifications.length, notificationsQuery.isFetching]);

  const handleMarkRead = (notificationId: string) => {
    markReadMutation.mutate(notificationId);
  };

  const handleArchive = (notificationId: string) => {
    archiveMutation.mutate(notificationId);
  };

  const handleMarkNeedsActionRead = () => {
    if (unreadNeedsActionCount > 0) {
      markSectionReadMutation.mutate("needs-action");
    }
  };

  const handleMarkRecentUpdatesRead = () => {
    if (unreadRecentUpdatesCount > 0) {
      markSectionReadMutation.mutate("recent-updates");
    }
  };

  const handleOpen = async (notification: UserNotificationResponseDto) => {
    if (notification.readAt === null) {
      try {
        await markReadMutation.mutateAsync(notification.id);
      } catch {
        // Keep navigation available even if marking read fails.
      }
    }
    navigate(resolveNotificationPath(notification));
  };

  return (
    <main className="mx-auto max-w-5xl px-2 py-6 sm:px-6">
      <div className="flex flex-col gap-2 border-b border-myteengig-border pb-4 md:flex-row md:items-end md:justify-between">
        <div className="mx-auto text-center">
          <h1 className="text-xl font-bold text-myteengig-text-primary">Notifications</h1>
        </div>
      </div>

      {notificationsQuery.isLoading ? <p className="py-10 text-myteengig-text-muted">Loading notifications...</p> : null}
      {notificationsQuery.error ? (
        <p className="py-10 text-sm font-medium text-red-600">
          {apiErrorToMessage(notificationsQuery.error, "Failed to load notifications.")}
        </p>
      ) : null}
      {notificationSummaryErrorMessage ? (
        <p className="pt-4 text-sm font-medium text-amber-700">
          {notificationSummaryErrorMessage} Showing the current page only.
        </p>
      ) : null}

      {!notificationsQuery.isLoading && !notificationsQuery.error && shouldShowCaughtUpState ? (
        <section className="py-12">
          <h2 className="text-lg font-semibold text-myteengig-text-primary">You are caught up</h2>
          <p className="mt-2 text-sm text-myteengig-text-muted">
            New requests, schedule changes, and selection updates will show up here.
          </p>
          <div className="mt-4">
            <Link to="/dashboard" className="text-sm font-semibold text-myteengig-primary hover:text-myteengig-primary-hover">
              Back to dashboard
            </Link>
          </div>
        </section>
      ) : null}

      {!notificationsQuery.isLoading && !notificationsQuery.error && shouldShowNotificationWorkspace ? (
        <div className="space-y-5 py-8">
          <div className="grid grid-cols-2 gap-3 md:flex md:flex-wrap md:items-center">
            <Button
              variant={activeView === "needs-action" ? "primary" : "outline"}
              size="sm"
              onClick={() => setActiveNotificationView("needs-action")}
              className="w-full"
            >
              Needs Action ({needsActionCount})
            </Button>
            <Button
              variant={activeView === "recent-updates" ? "primary" : "outline"}
              size="sm"
              onClick={() => setActiveNotificationView("recent-updates")}
              className="w-full"
            >
              Recent Updates ({recentUpdatesCount})
            </Button>
          </div>

          {activeView === "needs-action" ? (
            <section>
              <div className="mb-2 grid items-center gap-2 md:grid-cols-[1fr_auto_1fr]">
                <div className="hidden md:block" />
                <div className="flex items-center justify-center gap-2">
                  <h2 className="text-center text-lg font-semibold text-myteengig-text-primary">Needs Action</h2>
                  <InfoTooltip
                    label="Needs action info"
                    message="These are notifications that still need you to do something. Open the blue action button to finish the step. They leave this table after the underlying request or workflow is completed, resolved, or no longer needs your attention."
                  />
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={unreadNeedsActionCount === 0 || markSectionReadMutation.isPending}
                  onClick={handleMarkNeedsActionRead}
                  className="w-full md:w-auto md:justify-self-end"
                >
                  {markSectionReadMutation.isPending ? "Saving..." : "Mark All Read"}
                </Button>
              </div>
              <div className="overflow-hidden rounded-lg border border-myteengig-border bg-white">
                {needsActionCount === 0 ? (
                  <div className="px-5 py-6 text-sm text-myteengig-text-muted">No open action items right now.</div>
                ) : (
                  notifications.map((notification) => (
                    <NotificationRow
                      key={notification.id}
                      notification={notification}
                      pendingReadId={pendingReadNotificationId}
                      pendingArchiveId={pendingArchiveNotificationId}
                      onMarkRead={handleMarkRead}
                      onArchive={handleArchive}
                      onOpen={handleOpen}
                    />
                  ))
                )}
              </div>
              <Pagination
                page={pageInfo.number}
                pageSize={NOTIFICATIONS_PAGE_SIZE}
                hasNextPage={pageInfo.hasNext}
                hasPreviousPage={pageInfo.hasPrevious}
                resultsCount={notifications.length}
                onPageChange={(nextPage) => setPageForView("needs-action", nextPage)}
              />
            </section>
          ) : (
            <section>
              <div className="mb-2 grid items-center gap-2 md:grid-cols-[1fr_auto_1fr]">
                <div className="hidden md:block" />
                <div className="flex items-center justify-center gap-2">
                  <h2 className="text-lg font-semibold text-myteengig-text-primary">Recent Updates</h2>
                  <InfoTooltip
                    label="Recent updates info"
                    message="These are status updates and completed items that do not need a response. Mark them read once you have seen them, or use Archive All to clear this table."
                  />
                </div>
                <div className="grid w-full grid-cols-2 gap-3 md:flex md:w-auto md:justify-self-end">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={unreadRecentUpdatesCount === 0 || markSectionReadMutation.isPending}
                    onClick={handleMarkRecentUpdatesRead}
                    className="w-full"
                  >
                    {markSectionReadMutation.isPending ? "Saving..." : "Mark All Read"}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={recentUpdatesCount === 0 || archiveAllRecentUpdatesMutation.isPending}
                    onClick={() => archiveAllRecentUpdatesMutation.mutate()}
                    className="w-full"
                  >
                    {archiveAllRecentUpdatesMutation.isPending ? "Saving..." : "Archive All"}
                  </Button>
                </div>
              </div>
              <div className="overflow-hidden rounded-lg border border-myteengig-border bg-white">
                {recentUpdatesCount === 0 ? (
                  <div className="px-5 py-6 text-sm text-myteengig-text-muted">No recent updates yet.</div>
                ) : (
                  notifications.map((notification) => (
                    <NotificationRow
                      key={notification.id}
                      notification={notification}
                      pendingReadId={pendingReadNotificationId}
                      pendingArchiveId={pendingArchiveNotificationId}
                      onMarkRead={handleMarkRead}
                      onArchive={handleArchive}
                      onOpen={handleOpen}
                    />
                  ))
                )}
              </div>
              <Pagination
                page={pageInfo.number}
                pageSize={NOTIFICATIONS_PAGE_SIZE}
                hasNextPage={pageInfo.hasNext}
                hasPreviousPage={pageInfo.hasPrevious}
                resultsCount={notifications.length}
                onPageChange={(nextPage) => setPageForView("recent-updates", nextPage)}
              />
            </section>
          )}
        </div>
      ) : null}
    </main>
  );
};

export default NotificationsPage;
